import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getStatsOverview, type StatsTop } from "@/api/stats";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { fmtNumber } from "@/lib/format";

export function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["stats", "overview"],
    queryFn: getStatsOverview,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your CeroDeuda lead pipeline"
      />

      {error && <ErrorBanner error={error} />}

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner /> Loading stats…
        </div>
      )}

      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Kpi label="Total leads" value={data.total_leads} />
            <Kpi label="Last 30 days" value={data.leads_last_30_days} />
            <Kpi
              label="Avg / day (30d)"
              value={Math.round(data.leads_last_30_days / 30)}
            />
          </div>

          {/* Chart */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Leads per day (last 30 days)</div>
            </div>
            <div className="card-body">
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.leads_per_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      tickFormatter={(d: string) => d.slice(5)}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #e2e8f0",
                      }}
                    />
                    <Bar dataKey="count" fill="#355fff" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top tables */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <TopList title="Top UTM sources" rows={data.top_utm_sources} />
            <TopList title="Top states" rows={data.top_states} />
            <TopList
              title="Top sugar statuses"
              rows={data.top_sugar_statuses}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div className="mt-1 text-3xl font-semibold text-slate-900">
          {fmtNumber(value)}
        </div>
      </div>
    </div>
  );
}

function TopList({ title, rows }: { title: string; rows: StatsTop[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">{title}</div>
      </div>
      <div className="card-body p-0">
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">No data</div>
        ) : (
          <table className="table">
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.label ?? `__null_${i}`}>
                  <td className="truncate font-medium text-slate-800">
                    {r.label || <em className="text-slate-400">∅</em>}
                  </td>
                  <td className="w-20 text-right tabular-nums text-slate-600">
                    {fmtNumber(r.count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
